"use client";

import clsx from "clsx";
import { useTranslations } from "next-intl";
import { useIsLens } from "@/lib/audienceLens/AudienceLensContext";

/**
 * M3 — Investor lens evidence panel. Surfaces the unit economics an
 * investor cares about: take rate per trade, projected TPV (total
 * payment volume) at scale, customer LTV inputs, cost per acquired
 * trader, gross margin. Renders ONLY when URL lens is
 * `?lens=investor`; null for everyone else.
 *
 * Variants:
 *
 *   variant="reveal"        — anchored to T1/T2/T3 reveal screens
 *                              where the trade just closed. Shows the
 *                              per-trade economics + a "what this
 *                              looks like at scale" projection.
 */
export function InvestorEvidence({
  variant,
  notionalUsd,
  stageLabel,
  className,
}: {
  variant: "reveal" | "trade-body";
  notionalUsd?: number;
  /** When variant="trade-body", labels which stage of the trade this
   *  evidence panel anchors to (e.g. "Compliance gate", "LC issued",
   *  "Settlement"). Influences the leading copy only — the numbers are
   *  the same per-trade unit economics regardless of where in the arc
   *  the investor lens hits. */
  stageLabel?: string;
  className?: string;
}) {
  const t = useTranslations("investorEvidence");
  const isInvestor = useIsLens("investor");
  if (!isInvestor) return null;

  // Demo-illustrative take-rate split. Real per-trade revenue is the
  // function of bank-rail fees + platform fees + attestor fees.
  const takePct = 0.85;
  const takeUsd = notionalUsd ? Math.round((notionalUsd * takePct) / 100) : null;
  const monthlyTpv = 50_000_000;   // notional payment volume at year 1 target
  const monthlyTake = Math.round((monthlyTpv * takePct) / 100);
  const annualTake = monthlyTake * 12;

  if (variant === "reveal") {
    return (
      <div className={clsx("glass p-4", className)}>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
            {t("kicker")}
          </p>
          <span className="rounded-full border border-state-financed/30 bg-state-financed/8 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-state-financed">
            {t("lensChip")}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-3 text-[11px]">
          <div className="rounded border border-state-financed/30 bg-state-financed/8 px-3 py-2">
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("thisTradeTake")}</p>
            <p className="mt-0.5 font-display text-lg font-semibold text-state-financed">
              {takeUsd !== null ? `USD ${takeUsd.toLocaleString()}` : "—"}
            </p>
            <p className="mt-0.5 text-[10px] text-orbitra-muted">
              {t("notionalPct", { pct: takePct, notional: notionalUsd?.toLocaleString() ?? "—" })}
            </p>
          </div>
          <div className="rounded border border-state-traced/30 bg-state-traced/8 px-3 py-2">
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("monthlyTpvTarget")}</p>
            <p className="mt-0.5 font-display text-lg font-semibold text-state-traced">
              USD {(monthlyTpv / 1_000_000).toFixed(0)}M
            </p>
            <p className="mt-0.5 text-[10px] text-orbitra-muted">
              {t("monthlyTpvSubtitle", { count: Math.round(monthlyTpv / (notionalUsd ?? 1_500_000)).toLocaleString() })}
            </p>
          </div>
          <div className="rounded border border-state-verified/30 bg-state-verified/8 px-3 py-2">
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("runRateTake")}</p>
            <p className="mt-0.5 font-display text-lg font-semibold text-state-verified">
              USD {(annualTake / 1_000_000).toFixed(1)}M
            </p>
            <p className="mt-0.5 text-[10px] text-orbitra-muted">
              {t("runRateSubtitle")}
            </p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 text-[10px]">
          <ul className="rounded border border-state-financed/20 bg-orbitra-ink/40 px-3 py-2">
            <li className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("takeSplitTitle")}</li>
            <li className="mt-1 text-orbitra-paper">{t("takeSplitBank")} <span className="font-mono text-state-financed">60%</span></li>
            <li className="text-orbitra-paper">{t("takeSplitPlatform")} <span className="font-mono text-state-financed">25%</span></li>
            <li className="text-orbitra-paper">{t("takeSplitAttestor")} <span className="font-mono text-state-financed">15%</span></li>
          </ul>
          <ul className="rounded border border-state-traced/20 bg-orbitra-ink/40 px-3 py-2">
            <li className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("ltvInputsTitle")}</li>
            <li className="mt-1 text-orbitra-paper">{t("ltvAvgTrades")} <span className="font-mono text-state-traced">8</span></li>
            <li className="text-orbitra-paper">{t("ltvAvgNotional")} <span className="font-mono text-state-traced">USD 1.5M</span></li>
            <li className="text-orbitra-paper">{t("ltvRetention")} <span className="font-mono text-state-traced">92%</span></li>
          </ul>
        </div>

        <p className="mt-3 font-mono text-[9px] text-orbitra-muted">
          {t("illustrativeNote")}
        </p>
      </div>
    );
  }

  if (variant === "trade-body") {
    // Compact strip for mid-arc placements (compliance / LC issued /
    // settlement). Same unit-economics line surfaced at the stage.
    return (
      <div className={clsx("glass p-3", className)}>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
            {t("bodyKicker", { stage: stageLabel ?? t("bodyDefaultStage") })}
          </p>
          <span className="rounded-full border border-state-financed/30 bg-state-financed/8 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-state-financed">
            {t("lensChip")}
          </span>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-3 text-[11px]">
          <div className="rounded border border-state-financed/30 bg-state-financed/8 px-3 py-2">
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("bodyNotional")}</p>
            <p className="mt-0.5 font-display text-sm font-semibold text-orbitra-paper">
              USD {notionalUsd?.toLocaleString() ?? "—"}
            </p>
          </div>
          <div className="rounded border border-state-traced/30 bg-state-traced/8 px-3 py-2">
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("bodyTakeLabel", { pct: takePct })}</p>
            <p className="mt-0.5 font-display text-sm font-semibold text-state-traced">
              {takeUsd !== null ? `USD ${takeUsd.toLocaleString()}` : "—"}
            </p>
          </div>
          <div className="rounded border border-state-verified/30 bg-state-verified/8 px-3 py-2">
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{t("bodyLtvImpact")}</p>
            <p className="mt-0.5 font-display text-sm font-semibold text-state-verified">{t("bodyLtvValue")}</p>
            <p className="mt-0.5 text-[9px] text-orbitra-muted">{t("bodyLtvNote")}</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
